import paper from "paper";
import {
  each,
  eachL,
  filterL,
  go,
  head,
  mapL,
  minBy,
  reduce,
  takeAll,
  zipWithIndexL,
} from "fxjs/es";

export const getAllCpdPaths = (item) =>
  item.getItems({
    class: paper.CompoundPath,
  });

export const getAllPaths = (item) => {
  if (item instanceof paper.Path) {
    return [item];
  } else {
    return item.getItems({
      class: paper.Path,
    });
  }
};

export const removeInnerPath = (path) => {
  go(
    getAllPaths(path),
    filterL((path) => !path.clockwise),
    eachL((path) => path.remove()),
    takeAll
  );
};

export const removeBlankElements = (paper_scope) => {
  go(
    paper_scope.project.activeLayer.getItems({
      match: (x) =>
        x instanceof paper_scope.CompoundPath || x instanceof paper_scope.Group,
      children: (x) => x.length === 0,
    }),
    each((x) => x.remove())
  );
};

export const unitePath = (path_src, united_path) => {
  return !united_path
    ? path_src
    : united_path.unite(path_src, { insert: false });
};

export const unitedPaths = (src_paths) => {
  if (src_paths instanceof src_paths._project._scope.Path) {
    return src_paths;
  } else {
    let united_path = null;
    go(
      getAllPaths(src_paths),
      each((src_path) => {
        united_path = unitePath(src_path, united_path);
        src_path.remove();
      })
    );
    return united_path;
  }
};

export const getNumberOfSegments = (item) => {
  const paper_scope = item._project._scope;
  if (item instanceof paper_scope.Path) {
    return item.segments.length;
  } else {
    return go(
      item.getItems({
        class: paper_scope.Path,
      }),
      (paths) =>
        reduce(
          (acc, path) => {
            acc += path.segments.length;
            return acc;
          },
          0,
          paths
        )
    );
  }
};

export const getInternalAngle = (curve, is_show, color, position) => {
  // const v1 = curve.segment1.point.subtract(curve.segment2.point);
  const v1 = curve.getTangentAtTime(0.9).multiply(-1);
  const v2 = curve.next.getTangentAtTime(0.1);
  // const v2 = curve.next.segment2.point.subtract(curve.next.segment1.point);
  let angle = -v1.getDirectedAngle(v2);
  if (angle < 0) angle = 360 + angle;

  if (is_show) {
    const paper_scope = curve.path._project._scope;
    const text = new paper_scope.PointText(
      curve.segment2.point.add(curve.getNormalAtTime(0.9).multiply(position))
    );
    text.justification = "center";
    text.fillColor = color;
    text.fontSize = "9";
    text.content = `${Math.round(angle)}`;
  }
  return Math.round(angle);
};

const getMidPointOnCurve = (path, point_A, point_B) => {
  const offset_a = path.getOffsetOf(point_A);
  const offset_b = path.getOffsetOf(point_B);
  const mid_candidate_A = (offset_a + offset_b) / 2;
  const mid_candidate_B = (mid_candidate_A + path.length / 2) % path.length;
  const distance_A = Math.min(
    Math.abs(mid_candidate_A - offset_a),
    Math.abs(mid_candidate_A - offset_b)
  );
  const distance_B = Math.min(
    Math.abs(mid_candidate_B - offset_a),
    Math.abs(mid_candidate_B - offset_b)
  );

  return path.getPointAt(
    distance_A < distance_B ? mid_candidate_A : mid_candidate_B
  );
};

const getNNMidPoint = (path_A, path_B, point_A, point_B) => {
  // pingpong algorithm
  let avg_point_B = getMidPointOnCurve(path_B, point_A, point_B);
  const avg_point_A = path_A.getNearestLocation(avg_point_B).point;
  avg_point_B = path_B.getNearestLocation(avg_point_A).point;
  return [avg_point_A, avg_point_B];
};

const getNNPoint = (path_A, single_point_B) => {
  return [path_A.getNearestLocation(single_point_B).point, single_point_B];
};

export const getNearestNeighborPointSet = (
  crossing_location_arr,
  path_A,
  path_B,
  is_show_intersect
) => {
  let nn_arr;
  const cross_location_ctn = crossing_location_arr.length;
  if (cross_location_ctn < 1) {
    //1개 이하이면 이상한 상황이므로 에러
    // console.error("no crossing locations for bridge");
  } else if (cross_location_ctn === 1) {
    //만나는 점이 한개만 발생한 경우 바로 사용
    nn_arr = getNNPoint(path_A, crossing_location_arr[0].point);
  } else if (cross_location_ctn === 2) {
    //만나는 점이 2개인 경우 (그냥 평균 내서는 안되는게 커브에서 만나게 되면 공중에 떠 있는 점이나 들어간 점이 발생하게 됨)
    nn_arr = getNNMidPoint(
      path_A,
      path_B,
      crossing_location_arr[0].point,
      crossing_location_arr[1].point
    );
  } else {
    const min_curve_idx = go(
      crossing_location_arr,
      mapL((loc_B) =>
        path_A.getNearestLocation(loc_B.point).point.getDistance(loc_B.point)
      ),
      zipWithIndexL,
      minBy(([_, distance]) => distance),
      head
    );
    if (min_curve_idx === crossing_location_arr.length - 1) {
      nn_arr = getNNPoint(path_A, crossing_location_arr[min_curve_idx].point);
    } else {
      const idx_A = min_curve_idx % 2 === 0 ? min_curve_idx : min_curve_idx - 1;
      const idx_B = idx_A + 1;
      nn_arr = getNNMidPoint(
        path_A,
        path_B,
        crossing_location_arr[idx_A].point,
        crossing_location_arr[idx_B].point
      );
    }
  }

  if (is_show_intersect) {
    const paper_scope = path_A._project._scope;
    new paper_scope.Path.Circle(nn_arr[0], 5).set({
      fillColor: "green",
      name: "nn_point",
    });
    new paper_scope.Path.Circle(nn_arr[1], 5).set({
      fillColor: "yellow",
      name: "nn_point",
    });
  }
  return nn_arr;
};

export const getNearestDistancePingPong = (path_a, path_b) => {
  let point_a = path_a.segments[0];
  let point_b = path_b.getNearestPoint(point_a);
  point_a = path_a.getNearestPoint(point_b);
  point_b = path_b.getNearestPoint(point_a);
  return point_a.getDistance(point_b);
};
